import React, { useState, useRef } from 'react';
import { X, AlertCircle, ImagePlus } from 'lucide-react';
import { bugReportService, filesToAttachmentImages } from '../../services/bugReportService';
import { BugSeverity } from '../../types/bugReports';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const severityOptions: { value: BugSeverity; label: string; color: string }[] = [
  { value: 'critical', label: 'Critical', color: 'text-red-600 bg-red-50 border-red-300' },
  { value: 'high', label: 'High', color: 'text-orange-600 bg-orange-50 border-orange-300' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-600 bg-yellow-50 border-yellow-300' },
  { value: 'low', label: 'Low', color: 'text-blue-600 bg-blue-50 border-blue-300' }
];

const MAX_FILES = 6;

export const BugReportModal: React.FC<BugReportModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<BugSeverity>('medium');
  /** Embedded image data (data URLs) + label — real pixels, not file links */
  const [shots, setShots] = useState<{ name: string; dataUrl: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = async (list: FileList | null) => {
    if (!list?.length) return;
    const remaining = MAX_FILES - shots.length;
    if (remaining <= 0) return;
    const picked: File[] = [];
    for (let i = 0; i < list.length && picked.length < remaining; i++) {
      picked.push(list[i]);
    }
    if (picked.length === 0) return;
    setError('');
    try {
      const dataUrls = await filesToAttachmentImages(picked);
      const next = picked.map((f, i) => ({ name: f.name, dataUrl: dataUrls[i]! }));
      setShots((prev) => [...prev, ...next]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add image');
    }
  };

  const removeShot = (index: number) => {
    setShots((prev) => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setSeverity('medium');
    setShots([]);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await bugReportService.createBugReport({
        title,
        description,
        severity,
        attachment_images: shots.length > 0 ? shots.map((s) => s.dataUrl) : undefined
      });

      resetForm();
      onClose();
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feature request');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-orange-600" />
            <h2 className="text-lg font-semibold text-gray-900">Submit a Feature Request</h2>
          </div>
          <button
            onClick={handleClose}
            type="button"
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-300 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief summary of the feature request"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the feature you'd like, why it would help, and how you'd use it"
              required
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Screenshots (optional)
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Images are stored inline with your request (actual picture data, not a link). Up to {MAX_FILES}{' '}
              images — PNG, JPEG, WebP, or GIF, max 1.5 MB each.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
              multiple
              className="hidden"
              onChange={(e) => {
                void addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={shots.length >= MAX_FILES}
              className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 w-full justify-center disabled:opacity-50"
            >
              <ImagePlus className="w-4 h-4" />
              Add screenshots
            </button>
            {shots.length > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {shots.map((s, i) => (
                  <div
                    key={`${s.name}-${i}`}
                    className="relative rounded-lg border border-gray-200 bg-gray-50 overflow-hidden aspect-video"
                  >
                    <img
                      src={s.dataUrl}
                      alt=""
                      className="w-full h-full object-contain"
                    />
                    <button
                      type="button"
                      onClick={() => removeShot(i)}
                      className="absolute top-1 right-1 rounded bg-black/60 text-white text-[10px] px-1.5 py-0.5 hover:bg-black/80"
                    >
                      Remove
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] px-1 py-0.5 truncate">
                      {s.name}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Severity
            </label>
            <div className="grid grid-cols-4 gap-2">
              {severityOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setSeverity(option.value)}
                  className={`py-2 px-2 rounded border-2 text-xs font-medium transition-all ${
                    severity === option.value
                      ? `${option.color} border-current`
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !title || !description}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
